import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, FormControlLabel, IconButton, List, ListItem, ListItemText, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useNavigate, useParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import { SECTIONS, sectionText } from '../labels';
import { Loader, SectionTitle, useConfirm, useLoad, useNotify } from '../common';

const DEFAULT_ORDER = SECTIONS.map(s => s.value);

/** Create (/lojas/nova) or edit (/lojas/:id) a store and its aisle order. */
const StoreEditPage: React.FC = () => {
  const { id } = useParams();
  const isNew = !id || id === 'nova';
  const navigate = useNavigate();
  const notify = useNotify();
  const [confirm, confirmDialog] = useConfirm();
  const { data, error: loadError, loading, reload } = useLoad(
    async () => (isNew ? null : (await api.getStores()).find(s => s.publicId === id) ?? null),
    [id],
  );

  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [order, setOrder] = useState<T.StoreSection[]>(DEFAULT_ORDER);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const store = data ?? null;
  useEffect(() => {
    if (!store) return;
    setName(store.name);
    setIsDefault(store.isDefault);
    // Sections the store does not list yet go to the end
    if (store.sectionOrder.length > 0)
      setOrder(store.sectionOrder.concat(DEFAULT_ORDER.filter(s => store.sectionOrder.indexOf(s) < 0)));
  }, [store]);

  const back = () => navigate('/casa/compras/lojas');

  const swap = (a: number, b: number) => setOrder(o => {
    const copy = o.slice();
    const tmp = copy[a];
    copy[a] = copy[b];
    copy[b] = tmp;
    return copy;
  });

  const save = async () => {
    const trimmed = name.trim();
    if (!trimmed) { setError('Indica o nome da loja.'); return; }
    setError(null);
    setBusy(true);
    try {
      const request: T.SaveStoreRequest = { name: trimmed, isDefault, sectionOrder: order };
      if (store) await api.updateStore(store.publicId, request);
      else await api.createStore(request);
      notify('Loja guardada.', { severity: 'success' });
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!store) return;
    if (!await confirm({ title: 'Apagar', text: `Apagar a loja "${store.name}"?`, confirmText: 'Apagar', danger: true })) return;
    setBusy(true);
    try {
      await api.deleteStore(store.publicId);
      notify('Loja apagada.', { severity: 'success' });
      back();
    } catch (e: any) {
      setError(e.message ?? 'Ocorreu um erro.');
      setBusy(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <IconButton aria-label="Voltar" onClick={back}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 600 }}>{isNew ? 'Nova loja' : 'Editar loja'}</Typography>
      </Stack>
      <Loader loading={loading} error={loadError} onRetry={reload} hasData={data !== undefined}>
        {data !== undefined && !isNew && !store && <Alert severity="warning">Esta loja já não existe.</Alert>}
        {data !== undefined && (isNew || store) && (
          <Stack spacing={2} sx={{ maxWidth: 520 }}>
            <TextField
              label="Nome" placeholder="Ex.: Lidl, Talho do bairro" value={name} onChange={e => setName(e.target.value)}
              slotProps={{ htmlInput: { maxLength: 60 } }} autoFocus={isNew}
            />
            <FormControlLabel control={<Switch checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />} label="Loja habitual" />

            <Box>
              <SectionTitle>Ordem dos corredores</SectionTitle>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Pela ordem em que andas na loja. Usa ↑ e ↓ para mudar.
              </Typography>
              <Paper variant="outlined">
                <List dense disablePadding>
                  {order.map((section, index) => (
                    <ListItem
                      key={section}
                      divider
                      secondaryAction={
                        <>
                          <IconButton aria-label={`Subir ${sectionText(section)}`} disabled={index === 0} onClick={() => swap(index, index - 1)}>
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton edge="end" aria-label={`Descer ${sectionText(section)}`} disabled={index === order.length - 1} onClick={() => swap(index, index + 1)}>
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </>
                      }
                    >
                      <ListItemText primary={`${index + 1}. ${sectionText(section)}`} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Box>

            {error && <Alert severity="error">{error}</Alert>}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
              {store && <Button variant="outlined" color="error" onClick={remove} disabled={busy}>Apagar loja</Button>}
            </Box>
          </Stack>
        )}
      </Loader>
      {confirmDialog}
    </>
  );
};

export default StoreEditPage;
